import ChevronDown from "@/assets/ChevronDown.svg";
import ChevronRight from "@/assets/ChevronRight.svg";
import PropTypes from "prop-types";
import { Dropdown, Nav } from "react-bootstrap";
import { getMainMenu } from "@/functions/getMainMenu";
import Link from "next/link";
import { ensureTrailingSlash } from "@/functions/ensureTrailingSlash";
import React, { useState, useRef, useEffect } from "react";

export default function SiteHeaderNav({ className }) {
  const nav = getMainMenu();
  const [openMenus, setOpenMenus] = useState({});
  const [secondaryHeight, setSecondaryHeight] = useState(0);
  const secondaryRef = useRef(null);

  const [isMobile, setIsMobile] = useState(false);

  /* Detect mobile */
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1400);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  /* Secondary menu equal height */
  useEffect(() => {
    if (secondaryRef.current && Object.keys(openMenus).some((key) => key.startsWith("main-"))) {
      const height = secondaryRef.current.offsetHeight;
      setSecondaryHeight(height);
    }
  }, [openMenus]);

  /* DESKTOP: Hover behavior */
  const handleMouseEnter = (key) => {
    if (isMobile) return;

    setOpenMenus((prev) => {
      const newState = {};
      const parts = key.split("-");
      let currentPath = "";

      parts.forEach((part) => {
        currentPath = currentPath ? `${currentPath}-${part}` : part;
        newState[currentPath] = true;
      });

      Object.keys(prev).forEach((k) => {
        if (k.startsWith("main-")) newState[k] = true;
      });

      return newState;
    });
  };

  const handleDropdownMouseEnter = (key) => {
    if (!isMobile) {
      setOpenMenus((prev) => ({ ...prev, [key]: true }));
    }
  };

  const handleDropdownMouseLeave = () => {
    if (!isMobile) {
      setOpenMenus({});
      setSecondaryHeight(0);
    }
  };

  /* MOBILE: Accordion toggle */
  const toggleAccordion = (key) => {
    setOpenMenus((prev) => {
      const isOpening = !prev[key];

      // base state update
      const newState = { ...prev, [key]: isOpening };

      // if closing, remove ALL descendants
      if (!isOpening) {
        Object.keys(prev).forEach((k) => {
          // remove child, grandchild, great-grandchild keys
          if (k.startsWith(key + "-")) {
            delete newState[k];
          }
        });
      }

      return newState;
    });
  };



  /* Rendering Classes */
  const menuClassByDepth = {
    2: "dropdown-secondary-wrapper",
    3: "dropdown-tertiary-wrapper text-black",
    4: "dropdown-quaternary-wrapper text-black",
  };

  const itemClassByDepth = {
    2: "nav-items",
    3: "nav-items",
    4: "nav-items text-white",
  };

  /* Recursive submenu renderer */
  const renderMenuItems = (items, parentKey = "", depth = 2) =>
    items.map(({ title, url, new_tab, children = [] }, idx) => {
      const key = parentKey ? `${parentKey}-${idx}` : `${idx}`;
      const isOpen = !!openMenus[key];

      if (children.length > 0) {
        const submenuDepth = depth + 1;
        const submenuClass = menuClassByDepth[submenuDepth] || "";
        const heightStyle =
          submenuDepth >= 3 && secondaryHeight > 0
            ? { minHeight: `${secondaryHeight}px` }
            : {};

        return (
          <div
            className="nav-item dropdown-item dropdown-submenu"
            key={key}
            onMouseEnter={() => !isMobile && handleMouseEnter(key)}
          >
            <Nav.Item>
              {/* TEXT ALWAYS NAVIGATES (mobile + desktop) */}
              <Nav.Link
                href={ensureTrailingSlash(url)}
                target={new_tab ? "_blank" : "_self"}
                className={`${isOpen ? "active" : ""}`}
              >
                {title}
              </Nav.Link>

              {/* ARROW opens accordion on mobile */}
              <Dropdown.Toggle
                variant={false}
                aria-label={`${title} menu`}
                className={`icon-right ${isOpen ? "active" : ""}`}
                onClick={(e) => {
                  if (isMobile) {
                    e.preventDefault();
                    toggleAccordion(key);
                  }
                }}
              >
                <ChevronRight />
              </Dropdown.Toggle>
            </Nav.Item>

            <Dropdown.Menu show={isOpen} className={submenuClass} style={heightStyle}>
              {renderMenuItems(children, key, submenuDepth)}
            </Dropdown.Menu>
          </div>
        );
      }

      /* Leaf item */
      const itemClass = itemClassByDepth[depth] || "";
      return (
        <Dropdown.Item as={Nav.Item} key={key} className={itemClass}>
          <Nav.Link href={ensureTrailingSlash(url)} target={new_tab ? "_blank" : "_self"}>
            {title}
          </Nav.Link>
        </Dropdown.Item>
      );
    });

  /* Main menu */
  return (
    <Nav className={className}>
      {nav.map(({ title, children, url, new_tab }, k) => {
        const key = `main-${k}`;
        const open = !!openMenus[key];

        return children && children.length > 0 ? (
          <div
            className="dropdown"
            key={k}
            onMouseEnter={() => handleDropdownMouseEnter(key)}
            onMouseLeave={() => handleDropdownMouseLeave(key)}
          >
            <Nav.Item>
              {/* TEXT ALWAYS NAVIGATES */}
              <Nav.Link
                href={ensureTrailingSlash(url)}
                target={new_tab ? "_blank" : "_self"}
                className={open ? "active" : ""}
              >
                {title}
              </Nav.Link>

              {/* ARROW opens accordion on mobile */}
              <Dropdown.Toggle
                variant={false}
                aria-label={`${title} menu`}
                className={`primary-icon-right ${open ? "active" : ""}`}
                onClick={(e) => {
                  if (isMobile) {
                    e.preventDefault();
                    toggleAccordion(key);
                  }
                }}
              >
                <ChevronDown />
              </Dropdown.Toggle>
            </Nav.Item>

            <Dropdown.Menu
              show={open}
              className="dropdown-secondary-wrapper"
              ref={k === 0 ? secondaryRef : null}
            >
              {renderMenuItems(children, `${k}`, 2)}
            </Dropdown.Menu>
          </div>
        ) : (
          <Nav.Item key={k}>
            <Nav.Link href={ensureTrailingSlash(url)} target={new_tab ? "_blank" : "_self"}>
              {title}
            </Nav.Link>
          </Nav.Item>
        );
      })}

      <div className="externalBtn">
        <div className="d-flex justify-content-center align-items-center text-center mb-4">
          <Link href={"https://mytempagency.bluearrow.co.uk/s/login"}>
            <button className="bg-primary text-white btn mx-2">Candidate</button>
          </Link>
          <Link href={"https://myportal.bluearrow.co.uk/s/login"}>
            <button className="text-white btn mx-2 btn-custom">Client</button>
          </Link>
        </div>
      </div>
    </Nav>
  );
}

SiteHeaderNav.defaultProps = {
  className: "",
};

SiteHeaderNav.propTypes = {
  className: PropTypes.string,
};
